/**
 * 模板模板校验 —— 纯函数，零 Vue 依赖。
 *
 * 求值层的诊断回答的是「**算不算得出来**」（未注册求值器、抛异常、成环）。
 * 这里回答的是另一个问题：「**算出来的东西能不能真的打出来**」。
 *
 * 目前两类：
 *
 *   1. **必填输入既没连线也没默认值** —— 打出来就是空的，用户却看不到任何提示
 *   2. **值域越界** —— 下游不接受这个值（条码窄条宽度、分辨率、字号…）
 *
 * 结果直接产出求值层那套 `Diagnostic[]`，所以**不需要新的展示通道**：
 * 节点 header 的告警徽标、属性面板里的详情列表，早就准备好了。
 *
 * ## 为什么值域越界还要单独查
 *
 * 数值控件的拖拽会夹紧（`NpWidgetNumber` 里 `clamp()`），
 * 所以手工拖不出越界值。真正会越界的是**从外部载入的模板** ——
 * 存档、别的工具生成、手改过 JSON。那正是要防的场景。
 */
import type { GraphNodeModel, InputRow, PropertyRow, RowConstraint } from '../types/node'
import { sameHandle } from './handles'
import { findSocketIn } from './sockets'
import { connectPlacement } from '../types/socket'
import { MAX_SUBGRAPH_DEPTH } from './subgraph'
import type { DiagnosticSeverity } from '../types/node'
import type { Diagnostic } from './evaluate'

/** 求值时只关心边的两端与静音态 */
export interface ValidateEdge {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
  muted?: boolean
}

export interface ValidateParams {
  models: readonly GraphNodeModel[]
  edges: readonly ValidateEdge[]
  /**
   * 取某个端口**当前实际的值**（通常来自求值结果）。
   *
   * 给不出来时会退回行上的内联控件值 —— 至少把静态的越界抓出来。
   *
   * ⚠️ 名字**不能叫 `valueOf`**：那是 `Object.prototype` 上的方法，
   * 解构时会从原型链上取到它而不是我们传的 —— 调用时报
   * `Cannot convert undefined or null to object`，而且看不出跟命名有关。
   */
  readValue?: (nodeId: string, handleId: string) => unknown
  /**
   * 把这一层的问题**归到某个节点上**（子图用：归到包含它的组节点）。
   *
   * 内层节点在顶层画布上根本看不见，诊断挂它身上徽标不会亮。
   */
  attributeTo?: string
  /** 嵌套深度（内部用，防环） */
  depth?: number
}

/** 没有可用值：undefined / null / 空字符串都算 */
function isUnset(value: unknown): boolean {
  return value === undefined || value === null || value === ''
}

/** 合并约束与控件上的值域；约束优先 */
function rangeOf(
  row: InputRow | PropertyRow,
): { min?: number; max?: number } | null {
  const widget = row.widget
  const fromWidget =
    widget && widget.kind === 'number' ? { min: widget.min, max: widget.max } : {}
  const constraint: RowConstraint | undefined = row.constraint
  const min = constraint?.min ?? fromWidget.min
  const max = constraint?.max ?? fromWidget.max
  if (min === undefined && max === undefined) return null
  return { min, max }
}

function labelOf(row: InputRow | PropertyRow): string {
  return row.label ?? row.id
}

export function validateGraph(params: ValidateParams): Diagnostic[] {
  const { models, edges, readValue } = params
  const depth = params.depth ?? 0
  const owner = params.attributeTo
  const issues: Diagnostic[] = []

  /**
   * 推一条问题。
   *
   * 在子图里时改挂到**包含它的节点**上，并在文案前补内层 id ——
   * 否则「组里某个端口没接」这种提示根本没法定位。
   */
  function report(entry: { nodeId: string; severity: DiagnosticSeverity; message: string }) {
    issues.push(
      owner
        ? {
            nodeId: owner,
            severity: entry.severity,
            message: `[组内 ${entry.nodeId}] ${entry.message}`,
          }
        : entry,
    )
  }

  // 静音边按「不存在」算 —— 和求值层保持一致，
  // 否则会出现「校验说没事，但求值当成没连」的割裂。
  const activeEdges = edges.filter((edge) => !edge.muted)

  for (const model of models) {
    for (const row of model.rows) {
      if (row.kind !== 'input' && row.kind !== 'property') continue

      const handleId = row.kind === 'input' ? row.socket.id : row.id
      const connected =
        row.kind === 'input' &&
        activeEdges.some(
          (edge) =>
            edge.target === model.id && sameHandle(edge.targetHandle, handleId),
        )

      // ── 检查 1：必填输入既没连线也没默认值 ──
      if (row.kind === 'input' && !connected && isUnset(row.widget?.value)) {
        report({
          nodeId: model.id,
          severity: 'error',
          message: `「${labelOf(row)}」没有连线，也没有默认值 —— 打出来会是空的`,
        })
        // 都没值了，后面的值域检查没意义
        continue
      }

      // 连着线就拿求值结果；否则退回内联默认值
      const value =
        connected && readValue
          ? readValue(model.id, handleId)
          : row.widget?.value

      // ── 检查 2：值域 ──
      const range = rangeOf(row)
      if (range && typeof value === 'number') {
        if (range.min !== undefined && value < range.min) {
          report({
            nodeId: model.id,
            severity: 'error',
            message: `「${labelOf(row)}」的值 ${value} 小于下限 ${range.min}`,
          })
        } else if (range.max !== undefined && value > range.max) {
          report({
            nodeId: model.id,
            severity: 'error',
            message: `「${labelOf(row)}」的值 ${value} 超过上限 ${range.max}`,
          })
        }
      }

      // ── 检查 3：自定义规则（值域说不清的那些） ──
      const custom = row.constraint?.check?.(value)
      if (custom) {
        report({
          nodeId: model.id,
          severity: row.constraint?.severity ?? 'warning',
          message: `「${labelOf(row)}」${custom}`,
        })
      }
    }
  }

  /*
   * ── 检查 4：跨类型裁剪 ────────────────────────────────────────
   *
   * 结构化类型之间共享祖先就能连，传值时裁到那个祖先。
   * 但**只共享到最上层结构**时（`component` → `rich`，共同祖先只有 `struct`），
   * 实际传过去的字段几乎没有 —— 技术上合法，几乎肯定是接错了。
   *
   * 按这个项目一贯的取向：**能接上 + 明确警告**，而不是拦死。
   * 模板里接错了的表现是「输出不对」，不是崩溃 —— 先让它跑起来更有用。
   */
  for (const edge of activeEdges) {
    const from = findSocketIn(models, edge.source, edge.sourceHandle)
    const to = findSocketIn(models, edge.target, edge.targetHandle)
    if (!from || !to) continue

    const placement = connectPlacement(from, to)
    /*
     * ⚠️ **精确连接的端口**：连线时会被挡住，但**手改的文档 / 导入的模板**
     * 里可能已经存在这种边 —— 那是"装得上但语义不对"，必须报出来
     * （生产端可不会替人检查这个）。
     */
    if (!placement.ok && placement.reason === 'strict') {
      report({
        nodeId: edge.target,
        severity: 'error',
        message: `这条连线接在了一个**精确连接**的端口上（只接受完全相同的类型）—— 请改接或删掉`,
      })
      continue
    }
    if (placement.ok && placement.kind === 'slice' && placement.lossy) {
      report({
        nodeId: edge.target,
        severity: 'warning',
        message: `这条连线只共享到最上层结构「${placement.via}」，实际能传过去的字段很少 —— 大概率是接错了`,
      })
    }
  }

  /*
   * ── 递归子图 ──────────────────────────────────────────────────
   *
   * 内层节点在顶层画布上看不见，所以内层的问题**归到包含它的那个节点上** ——
   * 组节点的徽标才会亮。文案里保留内层 id 用于定位。
   *
   * 深度上限防的是自含的组（A 的图里又有 A）。
   */
  if (depth < MAX_SUBGRAPH_DEPTH) {
    for (const model of models) {
      if (!model.graph) continue
      issues.push(
        ...validateGraph({
          models: model.graph.nodes,
          edges: model.graph.edges,
          readValue,
          attributeTo: owner ?? model.id,
          depth: depth + 1,
        }),
      )
    }
  }

  return issues
}
